import { FocusMonitor } from '@angular/cdk/a11y';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self, ViewChild, computed, effect, signal } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTable, MatTableModule } from '@angular/material/table';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faGripLinesVertical } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { WorkflowActionTypes } from 'src/app/models/RestApiCaller/Enums/workflow-action-types';
import { RestApiAction } from 'src/app/models/RestApiCaller/actions/rest-api-action';
import { RestApiResponseVariableConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-response-variable-configuration';
import { ActionFormDialogResult } from './Models/action-form-dialog-result';
import { ContextualMenuActions } from './Models/contextual-menu-actions';
import { ActionFormDialogComponent } from './action-form-dialog/action-form-dialog.component';

/**
 * Composant de saisie des actions d'un évènement.
 * (Utilisable dans un formulaire et dans un MatFormField)
 */
@Component({
    selector: 'app-actions-form',
    imports: [MatTableModule, MatIconModule, MatMenuModule, MatButtonModule, CdkDropList, FaIconComponent, CdkDragHandle, CdkDrag, NgClass],
    templateUrl: './actions-form.component.html',
    styleUrl: './actions-form.component.scss',
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: ActionsFormComponent,
        },
    ],
})
export class ActionsFormComponent implements OnInit, OnDestroy, MatFormFieldControl<RestApiAction[]> {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faGripLinesVertical = faGripLinesVertical;

    /**
     * Liste des variables de la réponse.
     */
    @Input()
    public responseVariables: RestApiResponseVariableConfiguration[] = [];

    /**
     * Liste des actions du menu contextuel du tableau des actions.
     */
    public ContextualMenuActions = ContextualMenuActions;

    /**
     * Liste des types d'actions et leur libellé.
     */
    public actionTypes: Map<WorkflowActionTypes, string> = new Map<WorkflowActionTypes, string>();

    /**
     * Liste des colonnes affichées dans le tableau des actions.
     */
    displayedColumns: string[] = ['order', 'name', 'type'];

    /**
     * Liste des en-têtes affichées dans le tableau.
     */
    actions = signal<RestApiAction[]>([]);

    /**
     * Emet lorsque des changements surviennent dans le composant pour prevenir le MatFormField parent de déclencher le change detection.
     */
    stateChanges = new Subject<void>();

    /**
     * Associe les id des elements dont le contenu sera utilisé pour décrire ce composant à l'attribut aria.
     */
    @HostBinding('attr.aria-describedby') describedBy = '';

    /**
     * Définit les id des elements dont le contenu sera utilisé pour décrire ce composant.
     * @param ids Id des elements dont le contenu sera utilisé pour décrire ce composant.
     */
    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join('');
    }

    /**
     * Définit la valeur du contrôle.
     */
    @Input()
    set value(value: RestApiAction[]) {
        this.actions.set(value);
        this.stateChanges.next();
    }

    /**
     * Obtient la valeur du contrôle.
     */
    get value(): RestApiAction[] {
        return this.actions();
    }

    /**
     * Valeur de l'identifiant qui sera attribuée à la prochaine instance du contrôle.
     */
    static nextId = 0;

    /**
     * Définit la valeur du placeholder.
     */
    set placeholder(placeholder: string) {
        // Le placeholder n'est pas utilisé dans ce composant
    }

    /**
     * Obtient la valeur du placeholder.
     */
    get placeholder() {
        return null;
    }

    /**
     * Retourne une valeur indiquant si le contrôle est vide (absence d'en-tête)
     */
    get empty(): boolean {
        return this.actions().length !== 0;
    }

    /**
     * Retourne une valeur indiquant si le libellé doit être flottant.
     * (non géré)
     */
    get shouldLabelFloat(): boolean {
        return false;
    }

    /**
     * Obtient une valeur indiquant si ce contrôle est obligatoire.
     */
    @Input()
    get required() {
        return this._required;
    }

    /**
     * Définit une valeur indiquant si ce contrôle est obligatoire.
     */
    set required(value: boolean) {
        this._required = value;
        this.stateChanges.next();
    }

    /**
     * Valeur indiquant si le contrôle est obligatoire.
     */
    private _required = false;

    /**
     * Obtient une valeur indiquant si ce contrôle est désactivé.
     */
    @Input()
    get disabled() {
        return this._disabled;
    }

    /**
     * Définit une valeur indiquant si ce contrôle est désactivé.
     */
    set disabled(value: boolean) {
        this._disabled = value;

        let actionColumnIndex = this.displayedColumns.findIndex((c) => c === 'actions');

        if (value === false) {
            if (actionColumnIndex === -1) {
                this.displayedColumns.push('actions');
            }
        } else {
            if (actionColumnIndex !== -1) {
                this.displayedColumns.splice(actionColumnIndex, 1);
            }
        }
    }

    /**
     * Valeur indiquant si ce contrôle est désactivé.
     */
    private _disabled = false;

    /**
     * Valeur indiquant si le contrôle possède le focus.
     */
    _focused: boolean = false;

    /**
     * Obtient une valeur indiquant si le contrôle possède le focus.
     */
    get focused() {
        return this._focused;
    }

    /**
     * Définit une valeur indiquant si le contrôle possède le focus.
     */
    set focused(value: boolean) {
        this._focused = value;
        this.onTouched(value);
    }

    /**
     * Id du contrôle.
     */
    @HostBinding()
    id = `ActionsFormComponent-${ActionsFormComponent.nextId++}`;

    /**
     * Réference à l'élément MatTable du template.
     */
    @ViewChild(MatTable, { read: ElementRef, static: true })
    table: ElementRef;

    /**
     * Constructeur.
     * @param focusMonitor : Service utilisé pour écouter les changements de focus.
     * @param dialog : Service utilisé pour ouvrir des boites de dialogue.
     * @param ngControl : Reference au contrôle dans le formulaire.
     */
    constructor(
        private focusMonitor: FocusMonitor,
        public dialog: MatDialog,
        @Optional() @Self() public ngControl: NgControl
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
        computed(() => console.log('actions', this.actions));
        effect(() => {
            this.onChange(this.actions());
        });

        this.actionTypes.set(WorkflowActionTypes.ActivateStep, "Activation d'étape");
        this.actionTypes.set(WorkflowActionTypes.ApiCall, "Appel d'API");
        this.actionTypes.set(WorkflowActionTypes.TimerStop, 'Arrêt de minuteur');
        this.actionTypes.set(WorkflowActionTypes.ChangeStepState, "Changement d'état d'une étape");
        this.actionTypes.set(WorkflowActionTypes.SendMail, 'Envoi de mail');
        this.actionTypes.set(WorkflowActionTypes.SendSms, 'Envoi de SMS');
        this.actionTypes.set(WorkflowActionTypes.CompleteStep, "Finalisation d'étape");
        this.actionTypes.set(WorkflowActionTypes.CompleteWorkflow, 'Finalisation de workflow');
        this.actionTypes.set(WorkflowActionTypes.UpdateAttribute, "Mise à jour d'attribut");
        this.actionTypes.set(WorkflowActionTypes.OpenUrl, "Ouverture d'une URL");
        this.actionTypes.set(WorkflowActionTypes.SwitchFolderAsUnread, 'Passage du dossier en non lu');
        this.actionTypes.set(WorkflowActionTypes.ReminderCreation, 'Création de rappel');
        this.actionTypes.set(WorkflowActionTypes.ArchiveFolder, 'Archivage de dossier');
    }

    /**
     * Renseigne la valeur du contrôle.
     * (Appellé par le formulaire)
     * @param value : valeur du contrôle.
     */
    writeValue(value: RestApiAction[]): void {
        this.value = value;
    }

    /**
     * Fonction à appeler lorsque la valeur du contrôle change.
     */
    private onChange;

    /**
     * Inscrit une fonction à appeler lorsque la valeur du contrôle change.
     * (Appellé par le formulaire)
     * @param fn fonction à appeler lorsque la valeur du contrôle change.
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Fonction à appeler lorsque le contrôle gagne le focus.
     */
    private onTouched;

    /**
     * Inscrit une fonction à appeler lorsque le contrôle gagne le focus.
     * (Appellé par le formulaire)
     * @param fn fonction à appeler lorsque le contrôle gagne le focus.
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Specifie l'état 'désactivé' du contrôle.
     * (Appellé par le formulaire)
     * @param value : Valeur de l'état 'désactivé'.
     */
    setDisabledState(value: boolean): void {
        this.disabled = value;
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le contrôle.
     * (Appellé par le formulaire)
     * @param event evnement clic de la souris.
     */
    onContainerClick(event: MouseEvent): void {
        // On ne fait rien de particulier lorsque l'on clique sur le tableau.
    }

    /**
     * Retourne une valeur indiquant si le contrôle est en erreur.
     */
    get errorState(): boolean {
        // On considère que ce control n'est jamais en erreur.
        return false;
    }

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        this.focusMonitor.monitor(this.table).subscribe((focused) => {
            this.focused = !!focused;
            this.stateChanges.next();
        });
    }

    /**
     * Appellé lors de la destruction du contrôle.
     */
    ngOnDestroy(): void {
        this.focusMonitor.stopMonitoring(this.table);
        this.stateChanges.complete();
    }

    /**
     * Appelé lorsque l'utilisateur double-clique sur un élément de la liste.
     */
    onMouseDoubleClick(row: RestApiAction, event) {
        console.log('double clic', row);
        if (this.disabled) {
            this.displayAction(row);
        } else {
            this.onEditAction(row);
        }
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Editer' du menu contextuel.
     * @param editedAction : Instance de l'action a éditer.
     */
    public onEditAction(editedAction: RestApiAction): void {
        console.log('edit', editedAction);

        const dialogRef = this.dialog.open(ActionFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Édition de l'action",
                item: editedAction,
                variables: this.responseVariables,
                readonly: this.disabled,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: ActionFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Mise à jour de l'action.
                console.log('update action', dialogResult.item);
                this.actions.update((actions) => {
                    let updatedActions = RestApiAction.cloneArray(actions);
                    const actionIndex = actions.findIndex((x) => x.id === editedAction.id);
                    updatedActions.splice(actionIndex, 1, dialogResult.item);

                    return updatedActions;
                });
            }
        });
    }

    /**
     * Affiche une action en lecture seule.
     * @param action : Instance de l'action a afficher.
     */
    public displayAction(action: RestApiAction): void {
        console.log('display', action);

        const dialogRef = this.dialog.open(ActionFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Consultation de l'action",
                item: action,
                variables: this.responseVariables,
                readonly: this.disabled,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        // dialogRef.afterClosed().subscribe((dialogResult: ActionFormDialogResult) => {

        // });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton de création d'une action.
     */
    public onCreateAction(): void {
        console.log('create');

        const dialogRef = this.dialog.open(ActionFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'une action",
                item: new RestApiAction(),
                variables: this.responseVariables,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: ActionFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Création de l'action.
                console.log('create action', dialogResult.item);
                dialogResult.item.id = this.getNextId();
                dialogResult.item.order = this.getLastPosition();
                this.actions.update((actions) => {
                    let updatedActions = RestApiAction.cloneArray(actions);
                    updatedActions.push(dialogResult.item);

                    return updatedActions;
                });
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Dupliquer' du menu contextuel.
     * @param duplicatedAction : Instance de l'action a dupliquer.
     */
    public onDuplicateAction(duplicatedAction: RestApiAction): void {
        console.log('duplicate');
        duplicatedAction = RestApiAction.clone(duplicatedAction);
        duplicatedAction.id = null;
        duplicatedAction.order = null;

        const dialogRef = this.dialog.open(ActionFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'une action",
                item: RestApiAction.clone(duplicatedAction),
                variables: this.responseVariables,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: ActionFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Création de l'action.
                console.log('create action', dialogResult.item);
                dialogResult.item.id = this.getNextId();
                dialogResult.item.order = this.getLastPosition();
                this.actions.update((actions) => {
                    let updatedActions = RestApiAction.cloneArray(actions);
                    updatedActions.push(dialogResult.item);

                    return updatedActions;
                });
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Supprimer' du menu contextuel.
     * @param event : Instance de l'action a supprimer.
     */
    public onDeleteAction(event: RestApiAction): void {
        console.log('delete', event);
        this.actions.update((actions) => actions.filter((action) => action.id !== event.id));
    }

    /**
     * Appellé lorsque l'utilisateur modifie l'ordre d'une action.
     * @param event : Instance de l'évènement de drag&drop.
     */
    public onActionDrop(event: CdkDragDrop<string[]>) {
        let actions = this.actions();
        moveItemInArray(actions, event.previousIndex, event.currentIndex);
        let index = 1;
        actions.forEach((x) => {
            x.order = index++;
        });

        this.actions.set(RestApiAction.cloneArray(actions));
    }

    /**
     * Retourne l'id autogénéré pour l'action.
     * @returns un Id.
     */
    private getNextId() {
        let nextId = 1;

        if (this.actions().length !== 0) {
            nextId = this.actions().reduce((a1, a2) => (a1.id > a2.id ? a1 : a2)).id + 1;
        }

        return nextId;
    }

    /**
     * Retourne la position de la nouvelle action.
     * @returns une position à la fin de la liste.
     */
    private getLastPosition() {
        let lastPosition = 1;

        if (this.actions().length !== 0) {
            lastPosition = this.actions().reduce((a1, a2) => (a1.id > a2.id ? a1 : a2)).order + 1;
        }

        return lastPosition;
    }
}
